/**
 * Roles composable for role-based access control
 * Uses auth composable for role data
 */

import { useAuth } from '@composables/useAuth';
import { computed, shallowRef } from 'vue';

/**
 * Singleton instance of roles composable
 * Stored in shallowRef to ensure same instance is returned every time
 */
const rolesInstance = shallowRef<ReturnType<typeof createRolesInstance> | null>(null);

/**
 * Create roles composable instance
 * This function is only called once to create the singleton
 * All state and logic is encapsulated within this function
 */
function createRolesInstance() {
  const { roles } = useAuth();

  /**
   * Check if user has a specific role
   * @param role - Role name to check
   */
  const hasRole = computed(() => (role: string): boolean => {
    return roles.value.includes(role);
  });

  /**
   * Check if user has any of the specified roles
   * @param roleList - Array of role names
   */
  const hasAnyRole = computed(() => (roleList: string[]): boolean => {
    return roleList.some((role) => roles.value.includes(role));
  });

  /**
   * Check if user has all of the specified roles
   * @param roleList - Array of role names
   */
  const hasAllRoles = computed(() => (roleList: string[]): boolean => {
    return roleList.every((role) => roles.value.includes(role));
  });

  /**
   * Is user authorized for config management
   */
  const isAuthorizedForConfigManagement = computed(() => {
    return hasAnyRole.value(['implementation', 'clientSuccess']);
  });

  /**
   * Is admin user
   */
  const isAdminUser = computed(() => {
    return hasRole.value('admin');
  });

  /**
   * Is implementation user
   */
  const isImplementationUser = computed(() => {
    return hasRole.value('implementation');
  });

  /**
   * Is approver user
   */
  const isApproverUser = computed(() => {
    return hasRole.value('approver');
  });

  /**
   * Is bank user
   */
  const isBankUser = computed(() => {
    return hasRole.value('bank');
  });

  /**
   * Is client success user
   */
  const isClientSuccessUser = computed(() => {
    return hasRole.value('clientSuccess');
  });

  /**
   * Is client success or bank user
   */
  const isClientSuccessOrBankUser = computed(() => {
    return hasAnyRole.value(['bank', 'clientSuccess']);
  });

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isAuthorizedForConfigManagement,
    isAdminUser,
    isImplementationUser,
    isApproverUser,
    isBankUser,
    isClientSuccessUser,
    isClientSuccessOrBankUser,
  };
}

/**
 * Roles composable
 * Returns the same singleton instance every time
 * Provides computed properties for role checks
 */
export function useRoles() {
  if (!rolesInstance.value) {
    rolesInstance.value = createRolesInstance();
  }
  return rolesInstance.value;
}
